import React, { useState, useEffect } from 'react';
import { Card, Checkbox, Empty, Typography, Space, Tag, Tooltip, Alert } from 'antd';
import { AlertOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { isIncidentTypeMatch } from '../utils/regulatoryDeadlines';

const { Title, Text } = Typography;

const RegulatoryTriggerSelector = ({ 
  regulatoryRules = [], 
  incidentType = '', 
  selectedTriggers = [], 
  onChange,
  disabled = false,
  showAutoSuggestions = true 
}) => {
  const [autoSuggestedTriggers, setAutoSuggestedTriggers] = useState([]);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Calculate auto-suggestions when incident type or rules change
  useEffect(() => {
    if (showAutoSuggestions && incidentType && regulatoryRules.length > 0) {
      const matchingRules = regulatoryRules.filter(rule => 
        isIncidentTypeMatch(incidentType, rule.trigger)
      );
      const suggestedTriggers = matchingRules.map(rule => rule.trigger);
      setAutoSuggestedTriggers(suggestedTriggers);
      
      // Only auto-select suggested triggers on initial load if no triggers are currently selected
      // and user hasn't manually interacted with the selections
      if (!hasUserInteracted && selectedTriggers.length === 0 && suggestedTriggers.length > 0) {
        onChange(suggestedTriggers);
      }
    } else {
      setAutoSuggestedTriggers([]);
    }
  }, [incidentType, regulatoryRules, showAutoSuggestions, onChange, hasUserInteracted, selectedTriggers.length]);

  // Handle checkbox changes
  const handleTriggerChange = (trigger, checked) => {
    setHasUserInteracted(true); // Mark that user has interacted
    let newSelectedTriggers;
    if (checked) {
      newSelectedTriggers = [...selectedTriggers, trigger];
    } else {
      newSelectedTriggers = selectedTriggers.filter(t => t !== trigger);
    }
    onChange(newSelectedTriggers);
  };

  // Handle "None apply" option
  const handleNoneApplyChange = (checked) => {
    setHasUserInteracted(true); // Mark that user has interacted
    if (checked) {
      onChange([]);
    }
  };

  const isNoneSelected = selectedTriggers.length === 0;

  if (regulatoryRules.length === 0) {
    return (
      <Card>
        <Empty
          description={
            <div>
              <Text strong>No regulatory triggers configured</Text>
              <br />
              <Text type="secondary">
                Contact your administrator to set up regulatory compliance triggers for your organization.
              </Text>
            </div>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ marginBottom: '8px', color: '#39325b' }}>
          <AlertOutlined style={{ marginRight: '8px' }} />
          Regulatory Compliance Check
        </Title>
        <Text type="secondary">
          Review and confirm which regulatory requirements apply to this incident:
        </Text>
      </div>

      {showAutoSuggestions && autoSuggestedTriggers.length > 0 && (
        <Alert
          message="Smart Suggestions"
          description={
            <div>
              <Text>Based on your incident type "{incidentType}", we've pre-selected potential regulatory requirements:</Text>
              <div style={{ marginTop: '8px' }}>
                {autoSuggestedTriggers.map(trigger => (
                  <Tag key={trigger} color="blue" style={{ marginBottom: '4px' }}>
                    {trigger}
                  </Tag>
                ))}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 You can adjust these selections as needed
              </Text>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      <Space direction="vertical" style={{ width: '100%' }}>
        {regulatoryRules.map((rule, index) => {
          const isAutoSuggested = autoSuggestedTriggers.includes(rule.trigger);
          const isSelected = selectedTriggers.includes(rule.trigger);
          
          return (
            <div
              key={index}
              style={{
                padding: '12px',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
                backgroundColor: isAutoSuggested && isSelected ? '#e6f7ff' : 'white',
                borderColor: isAutoSuggested && isSelected ? '#91d5ff' : '#f0f0f0'
              }}
            >
              <Checkbox
                checked={isSelected}
                onChange={(e) => handleTriggerChange(rule.trigger, e.target.checked)}
                disabled={disabled}
                style={{ width: '100%' }}
              >
                <div style={{ marginLeft: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: '14px' }}>
                      {rule.trigger}
                      {isAutoSuggested && (
                        <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>
                          Auto-suggested
                        </Tag>
                      )}
                    </Text>
                    <Space>
                      <Tooltip title={`Deadline: ${rule.deadlineValue} ${rule.deadlineUnit}`}>
                        <Tag icon={<ClockCircleOutlined />} color="orange">
                          {rule.deadlineValue} {rule.deadlineUnit}
                        </Tag>
                      </Tooltip>
                    </Space>
                  </div>
                  <div style={{ marginTop: '4px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      <strong>Regulator:</strong> {rule.regulator} • 
                      <strong> Method:</strong> {Array.isArray(rule.method) ? rule.method.join(', ') : rule.method} • 
                      <strong> Sender:</strong> {rule.sender}
                    </Text>
                  </div>
                </div>
              </Checkbox>
            </div>
          );
        })}

        {/* None apply option */}
        <div
          style={{
            padding: '12px',
            border: '1px solid #f0f0f0',
            borderRadius: '6px',
            backgroundColor: isNoneSelected ? '#fff1f0' : 'white',
            borderColor: isNoneSelected ? '#ffccc7' : '#f0f0f0'
          }}
        >
          <Checkbox
            checked={isNoneSelected}
            onChange={(e) => handleNoneApplyChange(e.target.checked)}
            disabled={disabled}
          >
            <div style={{ marginLeft: '8px' }}>
              <Text strong style={{ fontSize: '14px' }}>
                None of these regulatory requirements apply
              </Text>
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  This incident does not trigger any regulatory reporting requirements
                </Text>
              </div>
            </div>
          </Checkbox>
        </div>
      </Space>

      {selectedTriggers.length > 0 && (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
          <Text strong style={{ color: '#52c41a' }}>
            <InfoCircleOutlined style={{ marginRight: '4px' }} />
            {selectedTriggers.length} regulatory requirement{selectedTriggers.length > 1 ? 's' : ''} selected
          </Text>
          <div style={{ marginTop: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Regulatory deadlines will be automatically calculated and tracked on the timeline
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
};

export default RegulatoryTriggerSelector; 